import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import {
  Card,
  Col,
  Descriptions,
  Image,
  Modal,
  Progress,
  Row,
  Typography,
  Tabs,
  Drawer,
  DatePicker,
} from "antd";
import ActionBar from "../components/ActionBar";
import "./style.css";
import useProcuringEntityPackage from "../../hooks/useProcuringEntityPackage";
import { useSubProjectPITChallenges, useSubProjectCSCChallenges } from "../../hooks/useDataHooks";
import SubProjectsMap from "../SubProjectsMap";
import Loader from "../Packages/componets/Loader";
import { useParams } from "react-router-dom/cjs/react-router-dom";
import { usePCTDateFilter } from "../../hooks/usePCTDateFilter";
import { usePITDateFilter } from "../../hooks/usePITDateFilter";
import { useCSCDateFilter } from "../../hooks/useCSCDateFilter";
import CustomList from "../components/List";
import ListItem from "../components/ListItem";
import { useSubProjectPCTChallenges } from "../../hooks/useDataHooks";
import { isoDateToHumanReadableDate } from "../../Util";
import ActivitiesSummary from "../SubProjects/ActivitiesSummary";

const { Title } = Typography;
const { TabPane } = Tabs;

const useColumnLayouts = (activeTab) => {
  return useMemo(() => {
    const baseLayout = { xxl: 8, xl: 10, lg: 10, md: 10, sm: 12, xs: 12 };
    
    if (activeTab === "PCT") {
      return {
        challengesLayout: { 
          xxl: 6, xl: 6, lg: 6, md: 6, sm: 24, xs: 24 
        },
        actionTakenLayout: { 
          xxl: 5, xl: 5, lg: 5, md: 5, sm: 24, xs: 24 
        },
        nameLayout: { 
          xxl: 5, xl: 5, lg: 5, md: 5, sm: 24, xs: 24 
        },
        reportDateLayout: { 
          xxl: 5, xl: 5, lg: 5, md: 4, sm: 24, xs: 24 
        },
      };
    }
  
    if (activeTab === "PIT") {
      return {
        challengesLayout: { 
          xxl: 6, xl: 6, lg: 6, md: 6, sm: 24, xs: 24 
        },
        actionTakenLayout: { 
          xxl: 5, xl: 5, lg: 5, md: 5, sm: 24, xs: 24 
        },
        nameLayout: { 
          xxl: 5, xl: 5, lg: 5, md: 5, sm: 24, xs: 24 
        },
        reportDateLayout: { 
          xxl: 4, xl: 4, lg: 4, md: 4, sm: 24, xs: 24 
        },
      };
    }
    
    return {
      challengesLayout: baseLayout,
      actionTakenLayout: { xxl: 0, xl: 0, lg: 0, md: 0, sm: 0, xs: 0 },
      wayForward: baseLayout,
      nameLayout: { xxl: 0, xl: 0, lg: 0, md: 0, sm: 0, xs: 0 },
      emailLayout: { xxl: 0, xl: 0, lg: 0, md: 0, sm: 0, xs: 0 },
      workSectionLayout: { xxl: 0, xl: 0, lg: 0, md: 0, sm: 0, xs: 0 }
    };
  }, [activeTab]);
};

// Added tab styles
const tabStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 500,
};

const iconStyle = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "9px",
  fontWeight: "700",
  color: "white",
};


function SubProject({ match }) {
  const {
    params: { packageId },
  } = match;
  const [singlePreviewVisible, setSinglePreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [activeTab, setActiveTab] = useState("PP");

  const { subprojectId } = useParams();
  const { loading, subprojects: subProjects, procuringEntityPackage } = useProcuringEntityPackage(packageId);
  const packageName = procuringEntityPackage?.name || "";
  
  
  const { 
    startDate: pctStartDate,
    endDate: pctEndDate,
    updateDateRange: updatePCTDateRange,
  } = usePCTDateFilter();
  
  const { pctChallenges, loading: pctLoading } = useSubProjectPCTChallenges(subprojectId, pctStartDate, pctEndDate);
  

  const { 
    startDate: pitStartDate,
    endDate: pitEndDate,
    updateDateRange: updatePITDateRange,
  } = usePITDateFilter();

  const { pitChallenges, loading: pitLoading } = useSubProjectPITChallenges(subprojectId, pitStartDate, pitEndDate);

  const { 
    startDate: cscStartDate,
    endDate: cscEndDate,
  } = useCSCDateFilter();

  const { cscChallenges, loading: cscLoading } = useSubProjectCSCChallenges(subprojectId, cscStartDate, cscEndDate);

  const { challengesLayout, actionTakenLayout, wayForward, nameLayout, reportDateLayout  } = useColumnLayouts(activeTab);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleRowClick = (item) => {
    setActiveItem(item);
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setActiveItem(null);
  };


  const findSubProjectById = (arr, id) => {
    return arr.find((item) => String(item.id) === String(id));
  };

  const selectedSubProject = findSubProjectById(Array.isArray(subProjects) ? subProjects : [], subprojectId);

  if (!selectedSubProject) {
    console.error("SubProject not found for the given ID:", subprojectId);
    // return <div style={{ padding: "20px", textAlign: "center" }}>SubProject not found</div>;
  }

  const images = (selectedSubProject?.progress?.[0]?.georeferenced_photos || [])
    .map((p) => ({ url: p?.image_url, caption: p?.description }))
    .filter((img) => img.url);
  const openSinglePreview = (url) => {
    setPreviewImage(url);
    setSinglePreviewVisible(true);
  };

  const handleSingleCancel = () => {
    setSinglePreviewVisible(false);
  };

  const progress = selectedSubProject?.progress?.[0] || {};
  console.log("progress", progress);
  const data = {
    targetQuantity: progress.target_quantity || "==/==",
    descriptionOfTarget: progress.description_of_target || "==/==",
    unitOfTarget: progress.unit_of_target || "==/==",
    quantificationOfProgress: progress.qualification_of_progress || "==/==",
    progressPercentage: progress.progress_towards_target || "==/==",
    designReviewTaken: progress.changes_taken || "==/==",
    remarks: progress.remarks || "==/==",
  };


  const getColumns = () => {
    if (activeTab === "PCT") {
      return [
        { ...challengesLayout, header: "Challenge" },
        { ...actionTakenLayout, header: "Action Taken" },
        { ...nameLayout, header: "Reported By" },
        { ...reportDateLayout, header: "Report Date" }
      ];
    }
    if (activeTab === "PIT") {
      return [
        { ...challengesLayout, header: "Challenge" },
        { ...actionTakenLayout, header: "Action Taken" },
        { ...nameLayout, header: "Reported By" },
        { ...reportDateLayout, header: "Report Date" }
      ];
    }
    return [
      { ...challengesLayout, header: "Challenge" },
      { ...wayForward, header: "Way Forward" },
    ];
  };


  const handlePCTDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      updatePCTDateRange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
    }
  };

  const handlePITDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      updatePITDateRange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
    }
  };

  const getTabContent = (key) => {
    switch (key) {
      case "PP":
        return (
          <div>
            <div className="table-title-container">
              <div>
                <h2 className="table-title">
                  SUB-PROJECT DETAILED PHYSICAL PROGRESS
                </h2>
                <h4 
                style={{ margin: "0" }}
                className="table-sub-title">
                  Construction Supervision Consultant Report
                </h4>
              </div>
              <p className="table-date-picker">Reported on {isoDateToHumanReadableDate(cscStartDate)} - {isoDateToHumanReadableDate(cscEndDate) || 'No date available'}</p>
            </div>
          </div>
        );
      case "CSC_CHALLENGES":
        return (
            <div className="table-title-container">
              <div>
                <h2 className="table-title">
                  CSC CHALLENGES & WAY FORWARD
                </h2>
                <h4 
                style={{ margin: "0" }}
                className="table-sub-title">
                  Construction Supervision Consultant Report
                </h4>
              </div>
              <p className="table-date-picker">Reported on {isoDateToHumanReadableDate(cscStartDate)} - {isoDateToHumanReadableDate(cscEndDate) || 'No date available'}</p>
            </div>
        );
      case "PIT":
        return (
          <div>
            <div
              className="table-title-container"
              style={{
                margin: "0",
              }}
            >
              <div>
                <h2 className="table-title">
                  PIT CHALLENGES & WAY FORWARD
                </h2>
                <h4 
                style={{ margin: "0" }}
                className="table-sub-title">
                  Project Implementation Team Report
                </h4>
              </div>
              <div style={{ marginRight: "0" }} className="table-date-picker">
                <DatePicker.RangePicker
                  value={pitStartDate && pitEndDate ? [moment(pitStartDate), moment(pitEndDate)] : null}
                  onChange={handlePITDateChange}
                  placeholder={["Start Date", "End Date"]}
                  size="middle"
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </div>
        );
      case "PCT":
        return (
          <div>
            <div
              className="table-title-container"
              style={{
                margin: "0",
              }}
            >
              <div>
                <h2 className="table-title">
                  PCT CHALLENGES & ACTIONS TAKEN
                </h2>
                <h4 
                style={{ margin: "0" }}
                className="table-sub-title">
                  Project Coordination Team Report
                </h4>
              </div>
              <div className="table-date-picker" style={{ marginRight: 0 }}>
                <DatePicker.RangePicker
                  value={pctStartDate && pctEndDate ? [moment(pctStartDate), moment(pctEndDate)] : null}
                  onChange={handlePCTDateChange}
                  placeholder={["Start Date", "End Date"]}
                  size="middle"
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <Loader fontSize={54} flexDirection="column" />;
  }

  return (
    <div style={{ padding: "20px" }}>
      <ActionBar
        actionButtonProp={{
          title: `${packageName}: ${selectedSubProject?.name} `,
        }}
      />
      <Card 
        style={{ border: "none", boxShadow: "none" }} 
        className="custom-container"
        hoverable={false}
        bordered={false}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab} className="prst-tabs">
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "PP" ? "#6B46C1" : undefined,
                }}
              >
                <span className="tab-icon" style={{ ...iconStyle, backgroundColor: "#6B46C1" }}>
                  PP
                </span>
                Physical Progress
              </span>
            }
            key="PP"
          >
            {getTabContent("PP")}
            {/* <Title
              level={4}
              style={{ textTransform: "uppercase", color: "#0f6788" }}
            >
              Sub-Project Detailed Physical Progress
            </Title> */}
            <Descriptions column={1} className="sub-project-details-descriptions">
              <Descriptions.Item
                label="Target (Quantity)"
                labelStyle={{ fontWeight: "bold", fontSize: "16px" }}
                contentStyle={{ fontSize: "16px" }}
              >
                {data.targetQuantity} {data.unitOfTarget}
              </Descriptions.Item>
              <Descriptions.Item
                label="Description of Target"
                labelStyle={{ fontWeight: "bold", fontSize: "16px" }}
                contentStyle={{ fontSize: "16px" }}
              >
                {data.descriptionOfTarget}
              </Descriptions.Item>
              <Descriptions.Item
                label="Quantification of Progress"
                labelStyle={{ fontWeight: "bold", fontSize: "16px" }}
                contentStyle={{ fontSize: "16px" }}
              >
                {data.quantificationOfProgress}
              </Descriptions.Item>
              <Descriptions.Item
                label="Progress Towards Target (%)"
                labelStyle={{ fontWeight: "bold", fontSize: "16px" }}
                contentStyle={{ fontSize: "16px" }}
              >
                <Progress percent={data.progressPercentage} />
              </Descriptions.Item>
              <Descriptions.Item
                label="Design Review Taken"
                labelStyle={{ fontWeight: "bold", fontSize: "16px" }}
                contentStyle={{ fontSize: "16px" }}
              >
                {data.designReviewTaken}
              </Descriptions.Item>
              <Descriptions.Item
                label="Remarks"
                labelStyle={{ fontWeight: "bold", fontSize: "16px" }}
                contentStyle={{ fontSize: "16px" }}
              >
                {data.remarks}
              </Descriptions.Item>
            </Descriptions>

            <div className="sub-project-details-csc-photos">
              <h2>
                Photos uploaded by CSC
              </h2>
              <Row gutter={[16, 16]}>
                {images.length > 0 ? (
                  images.map((image, index) => (
                    <Col key={index} xs={24} sm={12} md={8} lg={8}>
                      <div>
                        <Image
                          width="100%"
                          style={{
                            cursor: "pointer",
                            maxHeight: "200px",
                            objectFit: "cover",
                          }}
                          src={image.url}
                          preview={false}
                          onClick={() => openSinglePreview(image.url)}
                        />
                        <figcaption className="image-caption">
                          {image.caption}
                        </figcaption>
                      </div>
                    </Col>
                  ))
                ) : (
                  <Col xs={24} sm={12} md={8} lg={8}>
                    <div
                      style={{
                        width: "100%",
                        height: "200px",
                        backgroundColor: "#f0f0f0",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "16px",
                        color: "#999",
                      }}
                    >
                      No image Available
                    </div>
                  </Col>
                )}
              </Row>

              {/* Modal for single image preview */}
              <Modal
                visible={singlePreviewVisible}
                footer={null}
                onCancel={handleSingleCancel}
                width="50%"
              >
                <img
                  alt="Example"
                  style={{ width: "100%" }}
                  src={previewImage}
                />
              </Modal>
            </div>

            {/* Activities Summary Section */}
            <ActivitiesSummary subProjectId={subprojectId} />

            <div className="map-section">
              <SubProjectsMap subProjects={[selectedSubProject]} />
            </div>
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "CSC_CHALLENGES" ? "#38A169" : undefined,
                }}
              >
                <span className="tab-icon" style={{ ...iconStyle, backgroundColor: "#38A169" }}>
                  CSC
                </span>
                Challenges - CSC
              </span>
            }
            key="CSC_CHALLENGES"
          >
            {getTabContent("CSC_CHALLENGES")}
            <CustomList
              itemName="Challenges"
              items={cscChallenges}
              page={1}
              itemCount={cscChallenges.length}
              loading={cscLoading}
              headerLayout={getColumns()}
              renderListItem={({ item }) => (
                <ListItem
                  key={item.id}
                  name={`CSC Challenge #${item.id}`}
                  item={item}
                  isPackage={false}
                  onClick={() => handleRowClick(item)}
                  style={{ marginBottom: 12, cursor: "pointer" }}
                >
                  <Col {...challengesLayout} className="contentEllipse">
                    {item.challenge && item.challenge.length > 40
                      ? item.challenge.slice(0, 40) + "..."
                      : item.challenge}
                  </Col>
                  <Col {...wayForward} className="contentEllipse">{item.way_forward && item.way_forward.length > 40
                      ? item.way_forward.slice(0, 40) + "..."
                      : item.way_forward}</Col>
                </ListItem>
              )}
              showToolbar={false}
            />
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "PIT" ? "#38A169" : undefined,
                }}
              >
                <span className="tab-icon" style={{ ...iconStyle, backgroundColor: "#38A169" }}>
                  PIT
                </span>
                PIT
              </span>
            }
            key="PIT"
          >
            {getTabContent("PIT")}
            <div className="pct-table-container">
              <CustomList
                itemName="Challenges"
                items={pitChallenges}
                page={1}
                itemCount={pitChallenges.length}
                loading={pitLoading}
                headerLayout={getColumns()}
                renderListItem={({ item }) => (
                  <ListItem
                    key={item.id}
                    name={`PIT Challenge #${item.id}`}
                    item={item}
                    isPackage={false}
                    onClick={() => handleRowClick(item)}
                    style={{ marginBottom: 12, cursor: "pointer" }}
                  >
                    <Row gutter={[8, 0]} style={{ width: '100%' }}>
                      <Col {...challengesLayout} className="contentEllipse">
                        <div className="mobile-label">Challenge</div>
                        {item.challenge_description || 'No challenge description available'}
                      </Col>
                      <Col {...actionTakenLayout} className="contentEllipse">
                        <div className="mobile-label">Action Taken</div>
                        {item.action_taken || 'No action taken yet'}
                      </Col>
                      <Col {...nameLayout} className="contentEllipse">
                        <div className="mobile-label">Reported By</div>
                        {item.pit_report?.staff_name || item.staff_name || 'Unknown Reporter'}
                        {item.pit_report?.section && ` (${item.pit_report.section})`}
                      </Col>
                      <Col {...reportDateLayout} className="contentEllipse">
                        <div className="mobile-label">Report Date</div>
                        {item?.visit_date ? `${item?.visit_date}` : 'No report date available'}
                      </Col>
                      </Row>
                  </ListItem>
                )}
                showToolbar={false} // <-- Hide Toolbar for this list
              />
            </div>
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "PCT" ? "#ED8936" : undefined,
                }}
              >
                <span className="tab-icon" style={{ ...iconStyle, backgroundColor: "#ED8936" }}>
                  PCT
                </span>
                PCT Report
              </span>
            }
            key="PCT"
          >
            {getTabContent("PCT")}
            <div className="pct-table-container">
              <CustomList
                itemName="Challenges"
                items={pctChallenges}
                page={1}
                itemCount={pctChallenges.length}
                loading={pctLoading}
                headerLayout={getColumns()}
                renderListItem={({ item }) => (
                  <ListItem
                    key={item.id}
                    name={`PCT Challenge #${item.id}`}
                    item={item}
                    isPackage={false}
                    onClick={() => handleRowClick(item)}
                    style={{ marginBottom: 12, cursor: "pointer" }}
                  >
                    <Row gutter={[8, 0]} style={{ width: '100%' }}>
                      <Col {...challengesLayout} className="contentEllipse">
                        <div className="mobile-label">Challenge</div>
                        {item.challenge_description || 'No challenge description available'}
                      </Col>
                      <Col {...actionTakenLayout} className="contentEllipse">
                        <div className="mobile-label">Action Taken</div>
                        {item.action_taken || 'No action taken yet'}
                      </Col>
                      <Col {...nameLayout} className="contentEllipse">
                        <div className="mobile-label">Reported By</div>
                        {item.pct_report?.staff_name || item.staff_name || 'Unknown Reporter'}
                        {item.pct_report?.section && ` (${item.pct_report.section})`}
                      </Col>
                      <Col {...reportDateLayout} className="contentEllipse">
                        <div className="mobile-label">Report Date</div>
                        {item?.visit_date && item?.exit_date ? `${item?.visit_date} - ${item?.exit_date}` : 'No report date available'}
                      </Col>
                    </Row>
                  </ListItem>
                )}
                showToolbar={false} // <-- Hide Toolbar for this list
              />
            </div>
          </TabPane>
        </Tabs>

        <Drawer
          title="Challenge Details"
          placement="right"
          visible={drawerVisible}
          onClose={handleCloseDrawer}
          width={600}
        >
          <p>
            <strong>Challenge:</strong> {activeItem?.challenge_description || activeItem?.challenge}
          </p>
          {activeItem?.action_taken && (
            <p>
              <strong>Action Taken:</strong> {activeItem.action_taken}
            </p>
          )}
          {activeTab === "PCT" && activeItem?.pct_report?.staff_name && (
            <p>
              <strong>Reported by:</strong> {activeItem.pct_report.staff_name}
            </p>
          )}
          {activeTab === "PCT" && activeItem?.user?.email && (
            <p>
              <strong>Email:</strong> {activeItem.user.email}
            </p>
          )}
          {activeTab === "PCT" && activeItem?.pct_report?.section && (
            <p>
              <strong>Work Section:</strong> {activeItem.pct_report.section}
            </p>
          )}
          {activeTab === "PIT" && (activeItem?.staff_name || activeItem?.reported_by) && (
            <p>
              <strong>Reported by:</strong> {activeItem.staff_name || activeItem.reported_by}
            </p>
          )}
          {activeTab === "PIT" && (activeItem?.email || activeItem?.user?.email) && (
            <p>
              <strong>Email:</strong> {activeItem.email || activeItem.user?.email}
            </p>
          )}
          {activeTab === "PIT" && (activeItem?.section || activeItem?.works_category) && (
            <p>
              <strong>Work Section:</strong> {activeItem.section || activeItem.works_category}
            </p>
          )}
          {activeTab === "PIT" && activeItem?.pit_report?.report_date && (
            <p>
              <strong>Report Date:</strong> {activeItem.pit_report.report_date}
            </p>
          )}
          {activeTab === "PCT" && activeItem?.pct_report?.report_date && (
            <p>
              <strong>Report Date:</strong> {activeItem.pct_report.report_date}
            </p>
          )}
          {activeTab === "CSC_CHALLENGES" && activeItem?.package?.name && (
            <p>
              <strong>Package:</strong> {activeItem.package.name}
            </p>
          )}
          {activeTab === "CSC_CHALLENGES" && activeItem?.works_category && (
            <p>
              <strong>Works Category:</strong> {activeItem.works_category}
            </p>
          )}
          {activeTab === "CSC_CHALLENGES" && activeItem?.report_start_date && activeItem?.report_end_date && (
            <p>
              <strong>Report Period:</strong> {isoDateToHumanReadableDate(activeItem.report_start_date)} - {isoDateToHumanReadableDate(activeItem.report_end_date)}
            </p>
          )}
          {activeItem?.way_forward && (
            <p>
              <strong>Way Forward:</strong> {activeItem.way_forward}
            </p>
          )}
          <div>
              <h2
                style={{
                  marginBottom: "12px",
                  fontWeight: "bold",
                  fontSize: "16px",
                }}
              >
                Photos uploaded by {activeTab}
              </h2>
              <Row gutter={[16, 16]}>
                {activeItem?.photo_url ? (
                    <Col xs={24} sm={12} md={8} lg={8}>
                      <div>
                        <Image
                          width="100%"
                          style={{
                            cursor: "pointer",
                            maxHeight: "200px",
                            objectFit: "cover",
                          }}
                          src={activeItem?.photo_url}
                          preview={false}
                          onClick={() => openSinglePreview(activeItem?.photo_url)}
                        />
                        <figcaption className="image-caption">
                          {activeItem?.caption}
                        </figcaption>
                      </div>
                    </Col>
                ) : (
                  <Col xs={24} sm={12} md={8} lg={8}>
                    <div
                      style={{
                        width: "100%",
                        height: "200px",
                        backgroundColor: "#f0f0f0",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "16px",
                        color: "#999",
                      }}
                    >
                      No image Available
                    </div>
                  </Col>
                )}
              </Row>

              {/* Modal for single image preview */}
              <Modal
                visible={singlePreviewVisible}
                footer={null}
                onCancel={handleSingleCancel}
                width="50%"
              >
                <img
                  alt="Example"
                  style={{ width: "100%" }}
                  src={previewImage}
                />
              </Modal>
            </div>
        </Drawer>
      </Card>
    </div>
  );
}

export default SubProject;
